import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Bell, Check, CheckCheck, Trash2, X, ShoppingCart, RefreshCw, UserCheck, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_SOUND_DATA = 'data:audio/wav;base64,UklGRl9vT19teleQGFjdCAAAA';

const typeIcons = {
  new_order: ShoppingCart,
  order_status: RefreshCw,
  access_request: UserCheck,
  system: AlertCircle,
};

const typeColors = {
  new_order: 'text-blue-500',
  order_status: 'text-emerald-500',
  access_request: 'text-amber-500',
  system: 'text-purple-500',
};

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function groupNotifications(notifications) {
  const today = new Date().toDateString();
  const groups = { today: [], earlier: [] };
  for (const n of notifications) {
    const nDate = new Date(n.created_at).toDateString();
    if (nDate === today) {
      groups.today.push(n);
    } else {
      groups.earlier.push(n);
    }
  }
  return groups;
}

const NotificationCenter = () => {
  const { getAuthHeader } = useAuth();
  const { subscribe } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const audioRef = useRef(null);

  const playSound = useCallback(() => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 800;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch (e) {
      // Audio not available
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes, prefsRes] = await Promise.all([
        axios.get(`${API}/notifications?limit=50`, { headers: getAuthHeader() }),
        axios.get(`${API}/notifications/unread-count`, { headers: getAuthHeader() }),
        axios.get(`${API}/notification-preferences`, { headers: getAuthHeader() }).catch(() => ({ data: { sound_enabled: true } }))
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.count);
      audioRef.current = prefsRes.data.sound_enabled !== false;
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    }
  }, [getAuthHeader]);

  // Fetch on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Subscribe to real-time notifications
  useEffect(() => {
    const unsub = subscribe('notification', (data) => {
      const notif = data.notification;
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
      if (audioRef.current !== false) playSound();
    });
    return unsub;
  }, [subscribe, playSound]);

  // Close panel on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, { headers: getAuthHeader() });
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (e) {
      console.error('Failed to mark as read:', e);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { headers: getAuthHeader() });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to mark all as read:', e);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`, { headers: getAuthHeader() });
      const removed = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (removed && !removed.is_read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (e) {
      console.error('Failed to delete notification:', e);
    }
  };

  const clearAll = async () => {
    try {
      await axios.delete(`${API}/notifications`, { headers: getAuthHeader() });
      setNotifications([]);
      setUnreadCount(0);
    } catch (e) {
      console.error('Failed to clear notifications:', e);
    }
  };

  const groups = groupNotifications(notifications);

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full animate-pulse"
            data-testid="notification-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Notification Panel */}
      {isOpen && (
        <div
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[520px] bg-background border border-border rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead} data-testid="mark-all-read-btn">
                  <CheckCheck className="h-3.5 w-3.5 mr-1" />
                  Read all
                </Button>
              )}
              {notifications.length > 0 && (
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={clearAll} data-testid="clear-all-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {/* Notification List */}
          <ScrollArea className="flex-1 max-h-[440px]">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground" data-testid="notifications-empty">
                <Bell className="h-10 w-10 mb-3 opacity-30" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1">You'll see order updates here</p>
              </div>
            ) : (
              <div className="py-1">
                {groups.today.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Today</div>
                    {groups.today.map((n) => (
                      <NotificationItem key={n.id} notification={n} onRead={markAsRead} onDelete={deleteNotification} />
                    ))}
                  </>
                )}
                {groups.earlier.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mt-1">Earlier</div>
                    {groups.earlier.map((n) => (
                      <NotificationItem key={n.id} notification={n} onRead={markAsRead} onDelete={deleteNotification} />
                    ))}
                  </>
                )}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

const NotificationItem = ({ notification, onRead, onDelete }) => {
  const Icon = typeIcons[notification.type] || AlertCircle;
  const colorClass = typeColors[notification.type] || 'text-gray-500';

  return (
    <div
      className={`group flex gap-3 px-4 py-3 hover:bg-muted/50 transition-colors cursor-pointer ${
        !notification.is_read ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''
      }`}
      onClick={() => !notification.is_read && onRead(notification.id)}
      data-testid={`notification-item-${notification.id}`}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 mt-0.5 ${colorClass}`}>
        <Icon className="h-4.5 w-4.5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${!notification.is_read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>
            {notification.title}
          </p>
          {!notification.is_read && (
            <span className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-1.5" data-testid="unread-dot" />
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
        <p className="text-[11px] text-muted-foreground/60 mt-1">{timeAgo(notification.created_at)}</p>
      </div>

      {/* Delete on hover */}
      <button
        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-destructive/10 rounded"
        onClick={(e) => { e.stopPropagation(); onDelete(notification.id); }}
        data-testid={`delete-notification-${notification.id}`}
      >
        <X className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
  );
};

export default NotificationCenter;
